// src/pages/ProgramDaysView.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import programAPI from '../services/programApi';
import { videoAPI } from '../services/api';
import {
    Box, Typography, Card, CardContent, Button, Alert, CircularProgress,
    Chip, CardMedia, IconButton, useTheme, Drawer, useMediaQuery, Fab,
    Menu, MenuItem, Divider, Dialog, DialogContent
} from '@mui/material';
import {
    FitnessCenter, Article, EmojiEvents, Restaurant, PlayArrow,
    Menu as MenuIcon, Close, CalendarToday, CheckCircle, KeyboardArrowDown
} from '@mui/icons-material';
import UserNavbar from '../components/UserNavbar';
import SideMenu from '../components/SideMenu';
import Footer from '../components/Footer';
import VideoPlayer from '../components/VideoPlayer';

const ProgramDaysView = () => {
    const { programId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const theme = useTheme();
    const { isDarkMode } = useCustomTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

    const [program, setProgram] = useState(null);
    const [days, setDays] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingVideos, setLoadingVideos] = useState(false);
    const [error, setError] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const dropdownOpen = Boolean(anchorEl);

    // ✅ Video Player Dialog
    const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
    const [playingVideo, setPlayingVideo] = useState(null);

    useEffect(() => { fetchProgramDetails(); }, [programId]);

    const fetchProgramDetails = async () => {
        try {
            setLoading(true);
            setError('');
            const programData = await programAPI.getDetails(programId);

            if (programData.access === 'limited') {
                setError('Немате пристап до оваа програма. Купете ја за да продолжите.');
                setLoading(false);
                return;
            }

            setProgram(programData.program || programData);

            if (programData.days && programData.days.length > 0) {
                const sortedDays = programData.days.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
                setDays(sortedDays);
                if (sortedDays.length > 0) handleDaySelect(sortedDays[0]);
            }
        } catch (err) {
            setError('Грешка при вчитување на програмата. ' + (err.response?.data?.message || ''));
        } finally {
            setLoading(false);
        }
    };

    const handleDaySelect = async (day) => {
        try {
            setSelectedDay(day);
            setLoadingVideos(true);
            setAnchorEl(null);
            const videosData = await videoAPI.getByDay(day.id);
            setVideos(videosData.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)));
        } catch (err) {
            setError('Грешка при вчитување на видеата.');
            setVideos([]);
        } finally {
            setLoadingVideos(false);
        }
    };

    // ✅ Otvori Video Player
    const handleVideoClick = (video) => {
        setPlayingVideo(video);
        setVideoPlayerOpen(true);
    };

    // ✅ POPRAVEN menuItems - Blog naместо Dnevен Raspored
    const menuItems = [
        {
            id: 1, title: 'Купи Програми',
            description: 'Прегледај и купи тренинг програми креирани од експерти',
            icon: <FitnessCenter sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/buy-programs', color: '#ff7eb9', available: true, badge: 'Популарно'
        },
        {
            id: 2, title: 'Купи Диета',
            description: 'Здравствени диететски планови прилагодени за вашите фитнес цели',
            icon: <Restaurant sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-diets', color: '#4CAF50', available: true, badge: 'Препорачано'
        },
        {
            id: 3, title: 'Блог',
            description: 'Читај статии, совети и информации за фитнес и здравје',
            icon: <Article sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/user-blog', color: '#a5d8ff', available: true, badge: 'Ново'
        },
        {
            id: 4, title: 'Достигнувања',
            description: 'Следете ги вашите достигнувања и фитнес милници',
            icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 32 } }} />,
            path: '/achievements', color: '#ffd166', available: false, badge: 'Наскоро'
        }
    ];

    const handleItemClick = (item) => {
        setSelectedItem(item);
        if (isMobile) setMobileMenuOpen(false);
        if (!item.available) {
            alert('🚧 Оваа функционалност е во развој и наскоро ќе биде достапна!');
            return;
        }
        navigate(item.path);
    };

    // ===== LOADING =====
    if (loading) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    height: '50vh', backgroundColor: theme.palette.background.default
                }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
            </>
        );
    }

    // ===== ERROR =====
    if (error && !program) {
        return (
            <>
                <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>
                <Box sx={{
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    height: '50vh', backgroundColor: theme.palette.background.default, px: 2
                }}>
                    <Card sx={{
                        maxWidth: 500, backgroundColor: theme.palette.background.paper,
                        borderRadius: '16px', border: '1px solid rgba(255,126,185,0.2)'
                    }}>
                        <CardContent>
                            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                            <Button fullWidth variant="contained"
                                    onClick={() => navigate('/buy-programs')}
                                    sx={{ background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)', borderRadius: '10px', fontWeight: 'bold' }}>
                                Назад кон Програми
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </>
        );
    }

    return (
        <>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1000 }}><UserNavbar /></Box>

            <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>

                {/* ✅ Desktop Sidebar - POPRAVEN */}
                {!isMobile && (
                    <Box sx={{
                        width: isTablet ? '280px' : '320px',
                        backgroundColor: theme.palette.background.paper,
                        borderRight: `1px solid ${theme.palette.divider}`,
                        flexShrink: 0, position: 'sticky', top: '64px',
                        height: 'calc(100vh - 64px)', overflowY: 'auto', zIndex: 999
                    }}>
                        <SideMenu
                            menuItems={menuItems}
                            selectedItem={selectedItem}
                            onItemClick={handleItemClick}
                            onItemHover={(item) => { if (!isMobile) setSelectedItem(item); }}
                            programsCount={0}
                            userName={user?.firstName && user?.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : 'Корисник'}
                        />
                    </Box>
                )}

                {/* Mobile Drawer */}
                {isMobile && (
                    <>
                        <Drawer anchor="left" open={mobileMenuOpen}
                                onClose={() => setMobileMenuOpen(false)}
                                PaperProps={{ sx: { width: '280px', backgroundColor: theme.palette.background.paper, borderRight: `1px solid ${theme.palette.divider}` } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => setMobileMenuOpen(false)}><Close /></IconButton>
                            </Box>
                            <SideMenu
                                menuItems={menuItems}
                                selectedItem={selectedItem}
                                onItemClick={handleItemClick}
                                onItemHover={() => {}}
                                programsCount={0}
                                userName={user?.firstName && user?.lastName
                                    ? `${user.firstName} ${user.lastName}`
                                    : 'Корисник'}
                            />
                        </Drawer>
                        <Fab color="primary" onClick={() => setMobileMenuOpen(true)}
                             sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: 1000 }}>
                            <MenuIcon />
                        </Fab>
                    </>
                )}

                {/* ===== MAIN CONTENT ===== */}
                <Box sx={{
                    flex: 1, p: { xs: 2, sm: 3, md: 4 },
                    backgroundColor: theme.palette.background.default, overflow: 'auto'
                }}>
                    {/* Hero */}
                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                        <Button variant="outlined" startIcon={<Close />}
                                onClick={() => navigate('/buy-programs')}
                                sx={{
                                    mb: 3, borderRadius: '10px',
                                    borderColor: '#ff7eb9', color: '#ff7eb9',
                                    '&:hover': { bgcolor: 'rgba(255,126,185,0.1)', borderColor: '#ff4a97' }
                                }}>
                            Назад
                        </Button>

                        <Typography variant={isMobile ? 'h4' : 'h2'} sx={{
                            fontWeight: 900, mb: 1,
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                        }}>
                            {program?.name || 'Програма'}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: theme.palette.text.secondary,
                                mb: 2,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            {program?.description || ''}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip icon={<CalendarToday />}
                                  label={`${days.length} ${days.length === 1 ? 'ден' : 'денови'}`}
                                  sx={{ bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9', fontWeight: 'bold', border: '1px solid rgba(255,126,185,0.3)' }} />
                            <Chip icon={<CheckCircle />} label="Активна програма"
                                  sx={{ bgcolor: 'rgba(76,175,80,0.1)', color: '#4CAF50', fontWeight: 'bold', border: '1px solid rgba(76,175,80,0.3)' }} />
                        </Box>
                    </Box>

                    {/* Days Dropdown */}
                    {days.length > 0 && (
                        <Box sx={{ mb: 4 }}>
                            <Button variant="contained"
                                    endIcon={<KeyboardArrowDown />}
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                    sx={{
                                        width: { xs: '100%', sm: 'auto' },
                                        minWidth: { sm: 300 }, height: 56,
                                        background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                        fontSize: '1rem', fontWeight: 'bold',
                                        justifyContent: 'space-between',
                                        borderRadius: '12px', px: 3, textTransform: 'none',
                                        boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                            boxShadow: '0 8px 25px rgba(255,126,185,0.4)'
                                        }
                                    }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarToday />
                                    {selectedDay ? selectedDay.title : 'Избери ден'}
                                </Box>
                            </Button>

                            <Menu anchorEl={anchorEl} open={dropdownOpen}
                                  onClose={() => setAnchorEl(null)}
                                  PaperProps={{
                                      sx: {
                                          width: { xs: '90%', sm: 400 }, maxHeight: 400,
                                          backgroundColor: theme.palette.background.paper,
                                          border: `1px solid ${theme.palette.divider}`,
                                          borderRadius: '12px',
                                          boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.8)' : '0 8px 32px rgba(255,126,185,0.15)'
                                      }
                                  }}>
                                {days.map((day, index) => (
                                    <MenuItem key={day.id} onClick={() => handleDaySelect(day)}
                                              selected={selectedDay?.id === day.id}
                                              sx={{
                                                  py: 2, px: 3,
                                                  borderBottom: index < days.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                                  '&.Mui-selected': { backgroundColor: 'rgba(255,126,185,0.1)', '&:hover': { backgroundColor: 'rgba(255,126,185,0.15)' } },
                                                  '&:hover': { backgroundColor: 'rgba(255,126,185,0.08)' }
                                              }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            <Chip label={`Ден ${index + 1}`} size="small" sx={{
                                                bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9',
                                                fontWeight: 'bold', border: '1px solid rgba(255,126,185,0.3)'
                                            }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                                                    {day.title}
                                                </Typography>
                                                {day.description && (
                                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                        {day.description.substring(0, 50)}...
                                                    </Typography>
                                                )}
                                            </Box>
                                            {selectedDay?.id === day.id && <CheckCircle sx={{ color: '#4CAF50' }} />}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}

                    {/* Selected Day Info + Videos */}
                    {selectedDay && (
                        <Box>
                            <Box sx={{
                                mb: 3, p: 3,
                                backgroundColor: theme.palette.background.paper,
                                borderRadius: '16px',
                                border: '1px solid rgba(255,126,185,0.2)',
                                boxShadow: '0 4px 24px rgba(255,126,185,0.08)'
                            }}>
                                <Box sx={{ height: 4, borderRadius: 2, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)', mb: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.text.primary }}>
                                    📅 {selectedDay.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                    }}
                                >
                                    {selectedDay.description || ''}
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 3, borderColor: theme.palette.divider }} />

                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.text.primary }}>
                                🎬 Видеа ({videos.length})
                            </Typography>

                            {loadingVideos ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress sx={{ color: '#ff7eb9' }} />
                                </Box>
                            ) : videos.length > 0 ? (
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                                    gap: 2
                                }}>
                                    {videos.map((video, index) => (
                                        <Card key={video.id}
                                              onClick={() => handleVideoClick(video)} // ✅ Otvori player
                                              sx={{
                                                  cursor: 'pointer',
                                                  backgroundColor: theme.palette.background.paper,
                                                  borderRadius: '16px',
                                                  border: '1px solid rgba(255,126,185,0.15)',
                                                  overflow: 'hidden',
                                                  transition: 'all 0.3s ease',
                                                  '&:hover': {
                                                      transform: 'translateY(-6px)',
                                                      boxShadow: '0 12px 30px rgba(255,126,185,0.25)',
                                                      borderColor: 'rgba(255,126,185,0.4)'
                                                  }
                                              }}>
                                            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)' }} />
                                            <CardMedia component="div" sx={{
                                                height: 180,
                                                backgroundImage: video.thumbnailUrl
                                                    ? `url(${video.thumbnailUrl})` // ✅ R2 URL direktno
                                                    : 'none',
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                backgroundColor: isDarkMode ? '#2a2a2a' : '#f0f0f0',
                                                display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', position: 'relative'
                                            }}>
                                                <Box sx={{
                                                    width: 56, height: 56, borderRadius: '50%',
                                                    bgcolor: 'rgba(255,126,185,0.85)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    boxShadow: '0 4px 15px rgba(255,126,185,0.5)'
                                                }}>
                                                    <PlayArrow sx={{ fontSize: 36, color: 'white' }} />
                                                </Box>
                                                <Chip label={`${index + 1}`} size="small" sx={{
                                                    position: 'absolute', top: 8, left: 8,
                                                    bgcolor: 'rgba(255,126,185,0.9)', color: 'white', fontWeight: 'bold'
                                                }} />
                                            </CardMedia>
                                            <CardContent sx={{ p: 2 }}>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 'bold', mb: 0.5,
                                                    color: theme.palette.text.primary, fontSize: '1rem'
                                                }}>
                                                    {video.title}
                                                </Typography>
                                                {video.description && (
                                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                        {video.description}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>
                            ) : (
                                <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', borderRadius: '12px' }}>
                                    Нема достапни видеа за овој ден
                                </Alert>
                            )}
                        </Box>
                    )}

                    {!selectedDay && days.length > 0 && (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <CalendarToday sx={{ fontSize: 80, color: theme.palette.text.disabled, mb: 2 }} />
                            <Typography variant="h5" sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}>
                                Изберете ден од менито погоре
                            </Typography>
                        </Box>
                    )}

                    {days.length === 0 && (
                        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', borderRadius: '12px' }}>
                            Оваа програма сè уште нема денови
                        </Alert>
                    )}

                    <Box sx={{ height: { xs: '80px', md: '100px' } }} />
                </Box>
            </Box>

            {/* ✅ VIDEO PLAYER DIALOG */}
            <Dialog
                open={videoPlayerOpen}
                onClose={() => { setVideoPlayerOpen(false); setPlayingVideo(null); }}
                maxWidth="md" fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        backgroundColor: theme.palette.background.paper,
                        border: '2px solid rgba(255,126,185,0.3)',
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                    <IconButton
                        onClick={() => { setVideoPlayerOpen(false); setPlayingVideo(null); }}
                        sx={{ color: theme.palette.text.secondary }}>
                        <Close />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 0, pb: 2 }}>
                    {playingVideo && <VideoPlayer video={playingVideo} />}
                </DialogContent>
            </Dialog>

            <Footer />
        </>
    );
};

export default ProgramDaysView;